import { SentInterval } from '../../../../../services/reports/delivery-report.service';

/**
 * Periods and intervals for the dashboard's "Messages sent" overview. Everything here works in the
 * viewer's own time, and cuts buckets the way the server does (weeks start on Monday).
 */

export type SentPreset = 'today' | 'yesterday' | 'last7' | 'last30' | 'thisMonth' | 'lastMonth' | 'custom';

export const SENT_PRESETS: readonly { value: SentPreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'custom', label: 'Custom range' },
];

export const SENT_INTERVALS: readonly { value: SentInterval; label: string }[] = [
  { value: 'hour', label: 'Hourly' },
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];

/** The server draws at most this many buckets (a month of hours). */
export const MAX_BUCKETS = 744;

const DAY_MS = 24 * 60 * 60 * 1000;

export interface Period {
  from: Date;
  /** Not included. */
  to: Date;
}

function startOfDay(time: Date): Date {
  return new Date(time.getFullYear(), time.getMonth(), time.getDate());
}

function addDays(time: Date, days: number): Date {
  return new Date(time.getFullYear(), time.getMonth(), time.getDate() + days);
}

/** What a preset covers, from midnight. A period that reaches today ends now. */
export function presetPeriod(preset: Exclude<SentPreset, 'custom'>, now = new Date()): Period {
  const today = startOfDay(now);
  switch (preset) {
    case 'today':
      return { from: today, to: now };
    case 'yesterday':
      return { from: addDays(today, -1), to: today };
    case 'last7':
      return { from: addDays(today, -6), to: now };
    case 'last30':
      return { from: addDays(today, -29), to: now };
    case 'thisMonth':
      return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: now };
    case 'lastMonth':
      return { from: new Date(now.getFullYear(), now.getMonth() - 1, 1), to: new Date(now.getFullYear(), now.getMonth(), 1) };
  }
}

/** Does the period run up to now, so newer sends keep arriving in it? */
export function reachesNow(period: Period, now = new Date()): boolean {
  return period.to.getTime() >= now.getTime() - 2 * 60_000;
}

/** The start of the bucket a moment falls in. */
export function truncate(time: Date, interval: SentInterval): Date {
  const start = new Date(time);
  switch (interval) {
    case 'hour':
      start.setMinutes(0, 0, 0);
      return start;
    case 'day':
      return startOfDay(time);
    case 'week':
      return addDays(startOfDay(time), -((time.getDay() + 6) % 7));
    case 'month':
      return new Date(time.getFullYear(), time.getMonth(), 1);
  }
}

function next(start: Date, interval: SentInterval): Date {
  switch (interval) {
    case 'hour':
      return new Date(start.getTime() + 60 * 60 * 1000);
    case 'day':
      return addDays(start, 1);
    case 'week':
      return addDays(start, 7);
    case 'month':
      return new Date(start.getFullYear(), start.getMonth() + 1, 1);
  }
}

/** The start of every bucket that overlaps the period, stopping one past the server's limit. */
export function bucketStarts(period: Period, interval: SentInterval): Date[] {
  const starts: Date[] = [];
  for (
    let start = truncate(period.from, interval);
    start < period.to && starts.length <= MAX_BUCKETS;
    start = next(start, interval)
  ) {
    starts.push(start);
  }
  return starts;
}

/**
 * Worth offering for this period: within the server's limit, and more than one bar (hours always
 * count, so the first minutes of today still have a chart).
 */
export function intervalFits(period: Period, interval: SentInterval): boolean {
  const count = bucketStarts(period, interval).length;
  return count <= MAX_BUCKETS && (count >= 2 || interval === 'hour');
}

/** The interval a period reads best in: hours for a day or two, then days, weeks, months. */
export function autoInterval(period: Period): SentInterval {
  const span = period.to.getTime() - period.from.getTime();
  const best: SentInterval = span <= 2 * DAY_MS ? 'hour' : span <= 62 * DAY_MS ? 'day' : span <= 366 * DAY_MS ? 'week' : 'month';
  if (intervalFits(period, best)) return best;
  return SENT_INTERVALS.map((option) => option.value).find((value) => intervalFits(period, value)) ?? 'month';
}

// ── Inputs for a custom period ──

const pad = (value: number) => String(value).padStart(2, '0');

/** "2026-10-14" for a date input. */
export function dateInput(time: Date): string {
  return `${time.getFullYear()}-${pad(time.getMonth() + 1)}-${pad(time.getDate())}`;
}

/** "08:30" for a time input. */
export function timeInput(time: Date): string {
  return `${pad(time.getHours())}:${pad(time.getMinutes())}`;
}

/** A date input and a time input back to a moment, or null when either is unreadable. */
export function fromInputs(date: string, time: string): Date | null {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const clock = /^(\d{2}):(\d{2})$/.exec(time || '00:00');
  if (!day || !clock) return null;
  const moment = new Date(+day[1], +day[2] - 1, +day[3], +clock[1], +clock[2]);
  return Number.isNaN(moment.getTime()) ? null : moment;
}

// ── Words ──

const dayName = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const shortDay = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const monthYear = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' });
const shortMonth = new Intl.DateTimeFormat('en-GB', { month: 'short' });
const clockFormat = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });

/** A bucket in full: "Tue 14 Oct, 09:00–10:00", "Tue 14 Oct", "Week of 12 Oct", "October 2026". */
export function bucketLabel(start: Date, interval: SentInterval): string {
  switch (interval) {
    case 'hour':
      return `${dayName.format(start)}, ${clockFormat.format(start)}–${clockFormat.format(next(start, 'hour'))}`;
    case 'day':
      return dayName.format(start);
    case 'week':
      return `Week of ${shortDay.format(start)}`;
    case 'month':
      return monthYear.format(start);
  }
}

/** A bucket on the axis: "09:00" (the date at midnight), "14 Oct", "Oct" (the year in January). */
export function axisLabel(start: Date, interval: SentInterval): string {
  switch (interval) {
    case 'hour':
      return start.getHours() === 0 ? shortDay.format(start) : clockFormat.format(start);
    case 'day':
    case 'week':
      return shortDay.format(start);
    case 'month':
      return start.getMonth() === 0 ? String(start.getFullYear()) : shortMonth.format(start);
  }
}

/** The period in words: "Mon 13 Oct", "Tue 1 Sep to Wed 30 Sep", "Wed 8 Oct, 00:00 to now". */
export function periodLabel(period: Period, now = new Date()): string {
  const atMidnight = (time: Date) => time.getHours() === 0 && time.getMinutes() === 0;
  if (atMidnight(period.from) && atMidnight(period.to)) {
    const last = addDays(period.to, -1);
    return last <= period.from ? dayName.format(period.from) : `${dayName.format(period.from)} to ${dayName.format(last)}`;
  }
  const start = `${dayName.format(period.from)}, ${clockFormat.format(period.from)}`;
  if (reachesNow(period, now)) return `${start} to now`;
  const sameDay = startOfDay(period.from).getTime() === startOfDay(period.to).getTime();
  return `${start} to ${sameDay ? '' : `${dayName.format(period.to)}, `}${clockFormat.format(period.to)}`;
}
